import React from 'react';
import { DayCellContentArg } from '@fullcalendar/react';
import moment from 'moment';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import {
  DATE_TIME_FORMAT,
  datetimeFormat,
  isNextMonth,
  isPastMonth,
} from 'utils/datetime';
import { OFFICE_CLOSED, UNAVAILABLE } from 'utils/constants';
import { Shift, SyncExceptionShift } from 'interfaces/serviceScheduleException';
import { WorkingHour } from 'interfaces/clinicSchedule';
import ReactTooltip from 'react-tooltip';
import { groupBy, sortBy } from 'lodash';

export interface CellData {
  date: string;
  day: string;
  isClinicClosed: boolean;
  holidayName: string | null | undefined;
  isHoliday: boolean | undefined;
  isServiceClosed: boolean;
  isServiceException: boolean;
  shifts: Shift[];
  clinicWorkingHour: string | WorkingHour[];
  isSyncedException: boolean;
  isExistingTwoPmsAvailability?: boolean;
}

const DayCell = (
  props: DayCellContentArg & {
    dayInfo: CellData | null;
    selectedDate: string;
    onCellClicked: (date: CellData) => void;
    highlightedDate: string;
    resetHighlightedDate: () => void;
  }
) => {
  const {
    date,
    dayInfo,
    isToday,
    dayNumberText,
    selectedDate,
    isPast,
    highlightedDate,
    resetHighlightedDate,
    onCellClicked,
    isExistingTwoPmsAvailability,
  } = props;
  const formattedDate = moment(date).format('YYYY-MM-DD');

  const isSyncedExceptionValid = dayInfo?.isSyncedException;

  const isServiceDayOff = () => {
    if (dayInfo?.isServiceClosed) return true;

    return false;
  };

  const isClinicOff = () => {
    if (dayInfo?.isClinicClosed || dayInfo?.isHoliday) return true;

    if (Array.isArray(dayInfo?.clinicWorkingHour)) {
      const data = dayInfo?.clinicWorkingHour[0];

      if (!data?.start && !data?.end) {
        return true;
      }
    }

    return false;
  };

  const isServiceHourOutsideClinic = () => {
    if (
      dayInfo?.clinicWorkingHour &&
      dayInfo?.shifts &&
      !isServiceDayOff() &&
      !isClinicOff() &&
      isFutureInThisMonth
    ) {
      const clinicWorkingHour = dayInfo.clinicWorkingHour[0] as WorkingHour;
      const serviceWorkingHours = dayInfo.shifts;

      const isServicerStartBeforeClinicHour = serviceWorkingHours.find((item) =>
        moment(item.startTime, 'HH:mm:ss').isBefore(
          moment(clinicWorkingHour.start, 'HH:mm:ss')
        )
      );

      const isServicerEndAfterClinicHour = serviceWorkingHours.find((item) =>
        moment(item.endTime, 'HH:mm:ss').isAfter(
          moment(clinicWorkingHour.end, 'HH:mm:ss')
        )
      );

      return isServicerStartBeforeClinicHour || isServicerEndAfterClinicHour;
    }
  };

  const renderWorkingHour = () => {
    if (isNextMonth(formattedDate, selectedDate)) {
      return null;
    }

    if (isSyncedExceptionValid) {
      if (Array.isArray(dayInfo?.shifts) && dayInfo.shifts.length > 0) {
        const shifts = dayInfo.shifts as unknown as SyncExceptionShift[];
        const groupByOpId = groupBy(shifts, 'operatory.id');

        const MAX_ITEMS = 2;
        const groupOpKeys = Object.keys(groupByOpId);
        let maxOperatories = groupOpKeys.slice(0, MAX_ITEMS);

        if (isExistingTwoPmsAvailability && groupOpKeys.length <= MAX_ITEMS) {
          maxOperatories = Array.from(
            { length: MAX_ITEMS + 1 },
            (_, i) => maxOperatories[i] ?? null
          );
        }

        return (
          <div className={`w-full mt-0.8`}>
            {maxOperatories.map((operatoryId) => {
              const sortedTimes = sortBy(groupByOpId[operatoryId], (item) =>
                moment(item.startTime, DATE_TIME_FORMAT.HH_MM_SS).valueOf()
              );

              if (operatoryId === null) {
                return <div className="h-[20px]" />;
              }

              return (
                <div
                  key={`pms-availability-${operatoryId}`}
                  className="flex flex-row items-start mt-[8px] gap-[8px]"
                >
                  <span className="font-bold text-darkest-grey text-1.1 leading-[18px] text-left max-w-[60px]">
                    {groupByOpId[operatoryId]?.[0].operatory.name || ''}
                  </span>
                  <span className="min-w-[55px] rounded-full border-[0.5px] border-magenta-lighter px-[3px] text-[10px] text-magenta-lighter">{`${
                    sortedTimes.length === 1
                      ? 'Chair Hours'
                      : `${sortedTimes.length} Chair Hours`
                  }`}</span>
                </div>
              );
            })}
            <div className="text-left text-[20px]">
              {groupOpKeys.length > 2 && <span>...</span>}
            </div>
          </div>
        );
      }
    }

    if (dayInfo?.isHoliday) {
      return <span>{dayInfo.holidayName}</span>;
    }

    if (isClinicOff()) {
      return <span>{OFFICE_CLOSED}</span>;
    }

    if (isServiceDayOff()) {
      return <span>{UNAVAILABLE}</span>;
    }

    const shifts = dayInfo?.shifts ?? [];

    if (shifts.length > 0) {
      return shifts.map((shift) => {
        const startTime = moment(shift.startTime, 'HH:mm:ss').format('hh:mmA');
        const endTime = moment(shift.endTime, 'HH:mm:ss').format('hh:mmA');
        return <span key={shift.id}>{`${startTime} - ${endTime}`}</span>;
      });
    }
  };

  const renderDayNumber = () => {
    const firstDayOfNextMonth = moment()
      .clone()
      .add(1, 'months')
      .startOf('months');

    if (firstDayOfNextMonth.isSame(moment(date), 'days')) {
      return `${dayNumberText} ${firstDayOfNextMonth.format('MMM')}`;
    }

    return dayNumberText;
  };

  const isInThisMonth =
    !isPastMonth(formattedDate, selectedDate) &&
    !isNextMonth(formattedDate, selectedDate);

  const isFutureInThisMonth = !isPast && isInThisMonth;

  const isPractitionerExcpetionDisplayed = () =>
    dayInfo?.isServiceException && !isServiceDayOff() && isFutureInThisMonth;

  const handleOnCellClicked = () => {
    if (!isFutureInThisMonth || isClinicOff()) return;
    onCellClicked(dayInfo!);
  };

  const getCellClassName = () => {
    const clinicOffClassName = `bg-lightPink text-grey cursor-not-allowed`;
    const practitionerOffClassName = 'bg-[#F4FAFF] text-current';
    const syncedPMSExceptionClassName = 'bg-lightest-grey';

    if (isSyncedExceptionValid) {
      return syncedPMSExceptionClassName;
    }

    if (isClinicOff()) {
      return clinicOffClassName;
    }

    if (isServiceDayOff()) {
      return practitionerOffClassName;
    }
  };

  const renderCellType = () => {
    if (isSyncedExceptionValid) {
      return (
        <div className="text-center leading-[20px] rounded-full border-[0.5px] border-[#949494] px-[4px] text-[10px] text-bold text-[#B3B3B3]">
          PMS
        </div>
      );
    }

    return (
      <>
        {isPractitionerExcpetionDisplayed() && (
          <div className="bg-lightBlue w-[6px] h-[6px] rounded-full" />
        )}

        {isServiceHourOutsideClinic() && (
          <>
            <button data-for={formattedDate} data-tip="">
              <InfoIcon className="w-1.4 h-1.4" />
            </button>
            <ReactTooltip
              id={formattedDate}
              place="bottom"
              effect="solid"
              className="!bg-white !border-light-grey !rounded-[0.8rem] !w-auto !h-auto !px-[1rem] !py-[0.4rem] !opacity-100 z-[9999] flex justify-center items-center shadow-primary  before:hidden after:hidden !min-w-[11.8rem] min-h-[4.4rem]"
            >
              <div className="text-11 leading-[1.8rem] text-darkest-grey flex items-center font-normal">
                <div className="flex flex-col">
                  <span className="self-start">Practice Hour</span>
                  <span>
                    {datetimeFormat({
                      dateString: (dayInfo?.clinicWorkingHour[0] as WorkingHour)
                        .start!,
                      format: 'HH:mm:ss',
                      pattern: 'hh:mm A',
                    })}{' '}
                    -{' '}
                    {datetimeFormat({
                      dateString: (dayInfo?.clinicWorkingHour[0] as WorkingHour)
                        .end!,
                      format: 'HH:mm:ss',
                      pattern: 'hh:mm A',
                    })}
                  </span>
                </div>
              </div>
            </ReactTooltip>
          </>
        )}
      </>
    );
  };

  return (
    <div
      className={`relative h-full flex flex-col justify-between py-[0.5rem] px-[0.7rem] cursor-pointer border 
      ${
        highlightedDate === formattedDate
          ? 'border-magenta'
          : 'border-transparent'
      } 
      ${getCellClassName()} 
      ${
        isFutureInThisMonth
          ? 'hover:border-magenta'
          : 'cursor-not-allowed !bg-white'
      }
      ${dayInfo ? '' : 'cursor-not-allowed'}
      `}
      onClick={handleOnCellClicked}
      onMouseEnter={() => {
        if (highlightedDate && isFutureInThisMonth) {
          resetHighlightedDate();
        }
      }}
      onMouseLeave={() => {
        if (highlightedDate === formattedDate) {
          resetHighlightedDate();
        }
      }}
    >
      <div
        className={`font-bold text-right ${
          isFutureInThisMonth ? 'text-darkest-grey' : 'text-grey'
        } ${
          isToday
            ? 'bg-magenta w-[24px] h-[24px] leading-[24px] border-magenta rounded-full !text-center !text-white self-end'
            : ''
        }`}
      >
        <div className="absolute left-[10px] flex items-center gap-x-0.4">
          {renderCellType()}
        </div>
        <div className={`${isClinicOff() ? 'text-grey' : ''}`}>
          {renderDayNumber()}
        </div>
      </div>
      {isFutureInThisMonth && (
        <div className="flex flex-col items-center justify-center text-center font-normal">
          {renderWorkingHour()}
        </div>
      )}
    </div>
  );
};

export default DayCell;
